import { useTranslation } from "react-i18next";
import React, { useCallback, useState, useEffect, useMemo, Suspense } from "react";
import { createPortal } from "react-dom";
import ZSLogo from "public/icon/ZSLogo.svg?url";
import AskKuralButtonLogoDefault from "public/icon/AskKuralButtonLogoDefault.svg?url";
import AskKuralButtonLogoSelected from "public/icon/AskKuralButtonLogoSelected.svg?url";
import AskKuralButtonLogoHover from "public/icon/AskKuralButtonLogoHover.svg?url";
import { Avatar, Badge, Button, Divider, DownOutlined, Header, Popover, Segmented, Select, Space, Tooltip } from "src/assets/imports/antdComponents";
import { useDispatch, useSelector } from "react-redux";
import {
	setAllStudiesData,
	setDropdownMaster,
	setInterfaceLanguage,
	setIsNavDisabled,
	setRole,
	setSearchText,
	setSelectedCollaborators,
	setSelectedStudy,
	setSupportedLanguages,
	setUserProfile,
	setUserProfileId,
} from "src/pages/Studies/states/studiesSlice";
import { ILevel1, ITag } from "src/components/Searchbar/interface";
import { getInitials } from "src/pages/Studies/transformer";
import { useLocation, useNavigate } from "react-router-dom";
import {
	confirmationStatusType,
	confirmationTypes,
	FeatureKeysType,
	FeatureList,
	roleAccessType,
	SupportedLanguageType,
} from "src/pages/Studies/models/interfaces";
import { setManageStudyDetails } from "src/pages/ManageStudies/states/slice/manageStudySlice";
import { getThemeNames, loadTheme } from "src/config/themeConfig";
import { setSelectedTheme, setSelectedThemeName } from "src/pages/AdminCenter/states/themeManagementSlice";
import DynamicLogo from "src/components/DynamicLogo";
import Loading from "src/components/LoadingComp";
import i18n from "src/i18n";
import { useFeatures } from "src/config/featureManager";
import { adminMenuItems, developerMenuItems, newMenuItems, observerMenuItems } from "src/config/menuConfig";
import {
	resetAllChartsCacheFlat,
	resetAllChartsSummaryCacheFlat,
	resetAllLoadedCharts,
	resetSectionTotalQuestions,
	resetSelectedChartsForExportHash,
	setFilterArray,
	setSearchMode,
	setSelectedSegments___,
} from "src/pages/Insights/states/sectionalSummarySlice";
import {
	resetIdConfigChat,
	resetReferenceItems,
	setAllChats,
	setAllQueries,
	setAskKuralStudySelectDrawerVisible,
	setChatError,
	setConversationId,
	setIsChatAvailableForCurrentChat,
	setIsCompDisabled,
	setIsLoading,
	setIsProcessComplete,
	setNewQuery,
	setReferenceDrawerVisibility,
	setSelectedBranchId,
	setSelectedChatData,
	setSelectedChatId,
	setSelectedData,
	setSelectedDataMap,
	setSelectedQuery,
	setSelectedStudies,
	setSelectedStudiesMap,
	updateAbortStreamingAskKural,
} from "src/pages/NewAskKural/states/askKuralSlice";
import { ReferenceDrawerVisibilityType } from "src/pages/NewAskKural/models/interfaces";
import { resetAllWidgetProps } from "src/pages/NewAskKural/states/widgetSlice";
import "./style.css";
import { hasUnsavedChangesReports } from "src/pages/Reports/transformer";
import { setConfirmationData, setUnsavedChanges } from "src/pages/ManageStudies/states/slice/unsavedChangesSlice";
import { setAutosaveStatus, setSelectedReport } from "src/pages/Reports/states/reportsSlice";
import ConfirmationDialog from "src/components/ConfirmationDialog";
import { setIsUserGuideModalVisible } from "src/pages/UserGuide/states/userGuideSlice";
import { persistor, RootState } from "src/store";
import { useMsal } from "@azure/msal-react";
import { clearProfileCache } from "src/services/userProfileCache";
import Notification from "src/components/Notification";
import { clearCognitoToken, getCognitoAccessToken } from "src/services/cognitoAuth";
import { suppressCognitoAutoLogin, markCognitoSignedOut } from "src/services/cognitoAutoLogin";
import { setRememberedUser } from "src/utils/rememberedUser";
import { AskKuralVersionType } from "src/pages/NewAskKural/models/interfaces";
import { MessageTypes } from "src/pages/AdminCenter/models/interfaces";
import ProfileContext from "src/pages/ProfileContext";
import { setIsProfileContextModalVisible } from "src/pages/ProfileContext/states/profileContextSlice";
import { environment } from "src/config/environment";
import { APP_LICENSE_BANNER_COLOR, APP_LICENSE_EXPIRY_FALLBACK_TEMPLATE, composeAppLicenseMessage, formatRemaining, useAppLicenseStatus } from "src/components/AppLicenseExpiry/useAppLicenseStatus";
import { isEntraEnabled } from "src/config/loginMethods";

const UserGuide = React.lazy(() => import("src/pages/UserGuide"));

type TopNavbarLanguage = {
	code: string;
	name: string;
};

const TopNavbar: React.FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const themeNames = getThemeNames();
	const { t } = useTranslation();
	const { isFeatureAvailable } = useFeatures();

	const [open, setOpen] = useState(false);
	const [Logo, setLogo] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isAskKuralModalOpen, setIsAskKuralModalOpen] = useState(false);
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const [key, setKey] = useState(null);
	const { featureList, interfaceLanguage, isForbiddenModalOpen, isNavDisabled, metaDetails, role, selectedStudy, userProfile, userProfileId } = useSelector(
		(state: RootState) => state.studiesSlice,
	);
	const { selectedTheme, selectedThemeName } = useSelector((state: RootState) => state.themeManagementSlice);
	const { confirmationData, hasUnsavedChanges } = useSelector((state: RootState) => state.unsavedChangesSlice);
	const { autosaveStatus, selectedReport } = useSelector((state: RootState) => state.reportsSlice);
	const { isUserGuideModalVisible } = useSelector((state: RootState) => state.userGuideSlice);
	const { askKuralVersion, selectedQueriesConfig } = useSelector((state: RootState) => state.askKuralSlice);
	const clientName = environment.REACT_APP_CLIENT_NAME || "";
	const isAskKuralEnabled = isFeatureAvailable(FeatureKeysType.AskKural);
	const isMultiLingualSupportEnabled = isFeatureAvailable(FeatureKeysType.MultiLingualSupport);
	const isMyFeedEnabled = isFeatureAvailable(FeatureKeysType.MyFeed);
	const isStoryBoardEnabled = isFeatureAvailable(FeatureKeysType.Storyboard);
	const isUserGuideEnabled = isFeatureAvailable(FeatureKeysType.UserGuide);
	const isProfileContextEnabled = isFeatureAvailable(FeatureKeysType.ProfileContext);
	const isReflectiveModeCampaignEnabled = isFeatureAvailable(FeatureKeysType.ReflectiveModeCampaign);
	const isMonitoringAvailable = isFeatureAvailable(FeatureKeysType.UserActivityMonitoring);
	const isPersonasEnabled = isFeatureAvailable(FeatureKeysType.Personas);
	const isAdminTranscriptExportEnabled = isFeatureAvailable(FeatureKeysType.AdminTranscriptExport);
	const { isProfileContextModalVisible } = useSelector((state: RootState) => state.profileContextSlice);
	const {
		isDismissed: appLicenseIsDismissed,
		messageTemplate: appLicenseMessageTemplate,
		msRemaining: appLicenseMsRemaining,
		status: appLicenseStatus,
	} = useAppLicenseStatus();
	// Once the user dismisses the top banner (persisted), the app-license-expiry warning collapses
	// into a small red "!" badge on the avatar; the full message reappears at the top of the account
	// popover (above the name/email) so the warning is never fully lost.
	const showLicenseIndicator = appLicenseStatus === "expiring" && appLicenseIsDismissed;
	const licenseMessageText = showLicenseIndicator
		? composeAppLicenseMessage(appLicenseMessageTemplate, t(formatRemaining(appLicenseMsRemaining).key, formatRemaining(appLicenseMsRemaining).vars), t(APP_LICENSE_EXPIRY_FALLBACK_TEMPLATE))
		: null;

	const currentPath = location.pathname;
	const useFigmaTopNav = currentPath.startsWith("/personas") || currentPath.startsWith("/dt");

	const { instance } = useMsal();
	const shouldShowNotficationPopup = featureList?.some((feature) => feature.feature_name === FeatureList.ReflectiveMode && feature.approved === true);
	const isAdmin = role === roleAccessType.Admin;
	const isSupport = role === roleAccessType.Support;
	const isDeveloper = role === roleAccessType.Developer;
	const isAdminOrSupport = isAdmin || isSupport;
	const isObserver = role === roleAccessType.Observer;

	const menuItems = useMemo(() => {

		const updatedItem = newMenuItems?.map((item) => {
			if (item.key === "my-feed") {
				const updated = { ...item, disabled: !isMyFeedEnabled };
				return updated;
			}
			if (item.key === "insilico-landing") {
				const updated = { ...item, disabled: !isPersonasEnabled };
				return updated;
			}
			if (item.key === "storyboard" || item.key === "reports") {
				const updated = { ...item, disabled: !isStoryBoardEnabled };
				return updated;
			}
			return item;
		});

		const updatedAdminItems = adminMenuItems
			?.filter((item) => {
				// Hide monitoring if feature not available
				if (item.key === "monitoring" && !isMonitoringAvailable) {
					return false;
				}
				if (item.key === "transcriptExport" && !isAdminTranscriptExportEnabled) {
					return false;
				}
				return true;
			})
			.map((item) => {
				if (item.key === "featureManagement" && isSupport) {
					return {
						...item,
						disabled: true,
						tooltip: MessageTypes.INSUFFICIENT_PERMISSION,
					};
				}
				return item;
			});

		const menu = isAdminOrSupport ? updatedAdminItems : isDeveloper ? developerMenuItems : isObserver ? observerMenuItems : updatedItem;

		const result = menu
			?.filter((item) => {
				if (!item?.displayOnTopNavbarRadio) return false;
				if (isSupport && item?.supportEnabled === false) return false;
				if (item.key === "insilico-landing" && item.disabled) return false;
				return true;
			})
			.filter((item) => !((item?.key === "insilico-landing" || item?.key === "personas") && !isPersonasEnabled))
			.map((item) => ({
				label: item.label,
				value: item.path,
				disabled: item.disabled,
				tooltip: "tooltip" in item ? item.tooltip : undefined,
			}));

		return result;
	}, [
		newMenuItems,
		adminMenuItems,
		role,
		isMyFeedEnabled,
		isStoryBoardEnabled,
		isPersonasEnabled,
		isAdminOrSupport,
		isSupport,
		isDeveloper,
		isObserver,
		isMonitoringAvailable,
		isAdminTranscriptExportEnabled,
	]);

	const studiesMenuItems = ["/studies", "/insights", "/manage-studies", "/add-study", "/manage-segment", "/edit-metadata", "/view-metadata"];
	const reportsMenuItems = ["/reports", "/add-report", "/view-report"];

	const selectedMenuItem = useMemo(() => {
		if (studiesMenuItems.includes(currentPath) || currentPath.startsWith("/manage-studies/")) {
			return "/studies";
		}
		if (reportsMenuItems.includes(currentPath)) {
			return "/reports";
		}
		if (currentPath.startsWith("/personas") || currentPath.startsWith("/dt")) {
			return "/personas";
		}
		return currentPath;
	}, [currentPath]);

	const unsavedChanges = useMemo(() => {
		if (studiesMenuItems.includes(currentPath)) {
			return hasUnsavedChanges;
		} else if (reportsMenuItems.includes(currentPath)) {
			return selectedReport ? hasUnsavedChangesReports(autosaveStatus) : false;
		}
		return false;
	}, [autosaveStatus, currentPath, hasUnsavedChanges, selectedReport]);

	const resetSearch = () => {
		dispatch(
			setSearchMode({
				isActive: false,
				selectedChart: null,
				originalSection: null,
			}),
		);
	};
	const handleOpenProfileContext = useCallback(() => {
		dispatch(setIsProfileContextModalVisible(true));
		setOpen(false);
	}, []);

	const handleProfileChange = useCallback(
		(value: string) => {
			const selectedProfile = metaDetails?.profile_language_mapping?.find((profile) => profile.profile_name === value);
			if (selectedProfile) {
				dispatch(setUserProfile(selectedProfile.profile_name));
				dispatch(setUserProfileId(selectedProfile.profile_id));
			}

			dispatch(setSelectedStudy(null));
			dispatch(setManageStudyDetails({}));
			dispatch(setAllStudiesData([]));
			dispatch(setDropdownMaster([]));
			dispatch(setAllQueries([]));
			dispatch(setSelectedQuery(null));
			dispatch(setAllChats([]));
			dispatch(resetSectionTotalQuestions());
			dispatch(resetAllLoadedCharts());
			dispatch(resetAllWidgetProps());
			dispatch(setNewQuery(null));
			dispatch(setConversationId(null));
			dispatch(setIsCompDisabled(false));
			dispatch(setIsNavDisabled(false));
			dispatch(resetAllChartsCacheFlat());
			dispatch(resetAllChartsSummaryCacheFlat());
			dispatch(setSelectedSegments___([]));
			dispatch(setFilterArray([]));
			resetSearch();
			// Bypass the redux-persist throttle so the new profile is written to
			// localStorage immediately; otherwise a refresh/new tab within the
			// throttle window rehydrates the previous profile.
			persistor.flush();
			setOpen(false);
			navigate("/");
		},
		[dispatch, navigate, JSON.stringify(metaDetails?.profile_language_mapping)],
	);

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
	};

	const handleLogOut = async () => {
		// Block the UI immediately so the page can't be interacted with while the
		// (Cognito) revoke round-trip runs before the redirect actually fires.
		setIsLoggingOut(true);
		setOpen(false);

		// Scrub the profile cache before redirect so the next user on this device doesn't hydrate stale role/profile headers.
		const upn = instance.getActiveAccount()?.username || instance.getAllAccounts()[0]?.username;
		clearProfileCache(upn);

		// Remember who was logged in so the login page can offer "Continue as …"
		const email = metaDetails?.user_email || upn || "";
		const name = metaDetails?.fullName || "";
		if (email) {
			setRememberedUser({ email, name });
		}

		// Cognito session takes precedence over IDM/Entra — if Amplify has a session
		// the user signed in via the Hosted UI, so we tear that down and route back
		// to the in-app /login picker for the next sign-in.
		const cognitoToken = await getCognitoAccessToken();
		if (cognitoToken) {
			// Arm the guard FIRST so that when Cognito's /logout endpoint redirects
			// us back to /login, the auto-login flow resolves to the picker instead
			// of silently re-authenticating from a surviving Hosted-UI/IdP SSO cookie.
			suppressCognitoAutoLogin();
			markCognitoSignedOut();
			// clearCognitoToken() (signOut) redirects through Cognito's Hosted UI
			// /logout endpoint back to redirectSignOut (/login). Do NOT add a manual
			// window.location redirect here — it would clobber that navigation and
			// leave the Hosted-UI session alive (the bug we're fixing).
			await clearCognitoToken();
			return;
		}

		if (isEntraEnabled) {
			handleEntraLogOut();
		} else {
			window.location.href = environment.REACT_APP_SIGNOUT_URL;
		}
	};

	const handleEntraLogOut = async () => {
		try {
			await instance.logoutRedirect({
				postLogoutRedirectUri: window.location.origin, // Where to return after logout
				account: instance.getActiveAccount(), // Ensure we sign out the correct account
			});
		} catch (error) {
			console.error("Logout failed", error);
		}
	};

	const handleRoleChange = (selectedRole: string) => {
		dispatch(setRole(selectedRole));
		dispatch(setSelectedStudy(null));
		dispatch(setManageStudyDetails({}));
		dispatch(setAllStudiesData([]));
		dispatch(setDropdownMaster([]));
		dispatch(setAllChats([]));
		dispatch(setSelectedChatData(null));
		dispatch(setSelectedChatId(null));
		dispatch(setSelectedBranchId(null));
		dispatch(setIsChatAvailableForCurrentChat(null));
		dispatch(resetIdConfigChat());
		dispatch(setSelectedStudies([]));
		dispatch(setSelectedData([]));
		dispatch(setSelectedStudiesMap({}));
		dispatch(setSelectedDataMap({}));
		dispatch(resetReferenceItems());
		dispatch(setReferenceDrawerVisibility(ReferenceDrawerVisibilityType.hidden));
		dispatch(setAskKuralStudySelectDrawerVisible(false));
		dispatch(updateAbortStreamingAskKural(null));
		dispatch(resetSectionTotalQuestions());
		dispatch(resetAllLoadedCharts());
		dispatch(resetAllWidgetProps());
		dispatch(setAllQueries([]));
		dispatch(setSelectedQuery(null));
		dispatch(setNewQuery(null));
		dispatch(setConversationId(null));
		dispatch(setIsCompDisabled(false));
		dispatch(setIsLoading(false));
		dispatch(setIsProcessComplete(false));
		dispatch(setChatError(false));
		dispatch(setIsNavDisabled(false));
		dispatch(resetAllChartsCacheFlat());
		dispatch(resetAllChartsSummaryCacheFlat());
		dispatch(setSelectedSegments___([]));
		dispatch(setFilterArray([]));
		resetSearch();

		if (selectedRole === roleAccessType.Admin) {
			i18n.changeLanguage(SupportedLanguageType.English);
			localStorage.setItem("i18nextLng", SupportedLanguageType.English);
			dispatch(setInterfaceLanguage({ name: "English", code: SupportedLanguageType.English }));
		} else {
			const storedLanguage = localStorage.getItem("i18nextLng") || SupportedLanguageType.English;
			const selectedLang = metaDetails?.languages?.find((lang: TopNavbarLanguage) => lang.code === storedLanguage) || metaDetails?.languages?.[0];
			i18n.changeLanguage(selectedLang?.code || SupportedLanguageType.English);
			localStorage.setItem("i18nextLng", selectedLang?.code || SupportedLanguageType.English);
			dispatch(setInterfaceLanguage(selectedLang || { name: "English", code: SupportedLanguageType.English }));
		}

		// Bypass the redux-persist throttle so the new role/language is written to
		// localStorage immediately; otherwise a refresh/new tab within the throttle
		// window rehydrates the previous role.
		persistor.flush();
		setOpen(false);
		navigate(isAdminOrSupport ? "/admin-center" : "/");
	};

	const handleLanguageChange = (selectedLanguage: string) => {
		const selectedLang = metaDetails?.languages?.find((lang: TopNavbarLanguage) => lang.code === selectedLanguage);
		dispatch(setInterfaceLanguage(selectedLang || { name: "English", code: SupportedLanguageType.English }));
		dispatch(setSupportedLanguages(Array.from(new Set([SupportedLanguageType.English, selectedLang?.code]))));
		i18n.changeLanguage(selectedLang?.code || SupportedLanguageType.English);
		localStorage.setItem("i18nextLng", selectedLang?.code || SupportedLanguageType.English);
		// Bypass the redux-persist throttle so the new supported languages are
		// written to localStorage immediately.
		persistor.flush();
		setOpen(false);
		navigate("/");
	};

	const handleThemeChange = useCallback((value) => {
		const theme = value ? themeNames[0] : themeNames[1];
		const loadedThemeData = loadTheme(theme);
		dispatch(setSelectedTheme(loadedThemeData));
		dispatch(setSelectedThemeName(theme));
		localStorage.setItem("selectedThemeName", theme);
	}, []);

	const popContent = (
		<div className={showLicenseIndicator && licenseMessageText ? "w-[21rem]" : "w-60"}>
			{showLicenseIndicator && licenseMessageText && (
				<>
					<div className="flex items-start gap-2" style={{ color: APP_LICENSE_BANNER_COLOR }}>
						<i className="material-symbols-outlined" style={{ fontSize: "1.5rem", lineHeight: 1.2 }} aria-hidden="true">
							info
						</i>
						<span className="text-[x-small] font-normal leading-snug">{licenseMessageText}</span>
					</div>
					<Divider className="my-4" />
				</>
			)}
			<div className="flex items-center">
				<Avatar size="small" className="navbar-icon flex items-center justify-center mr-4">
					{getInitials(metaDetails?.fullName)}
				</Avatar>
				<div className="flex flex-col">
					<span className="text-base-14 font-medium secondary-color">{metaDetails?.fullName}</span>
					<span className="text-[x-small] tertiary-color email-text">{metaDetails?.user_email}</span>
				</div>
			</div>
			<Divider className="my-4" />
			{metaDetails?.roles?.length > 0 && (
				<>
					<div className="flex flex-col">
						<label className="text-[x-small] tertiary-color mb-1">{t("Role")}:</label>
						<Select
							value={role}
							title="Role"
							className="py-[0.2rem] custom-dropdown text-base-1 custom-select"
							popupClassName="customTop-dropdown"
							onChange={handleRoleChange}
							options={metaDetails.roles.map((roleType) => ({
								value: roleType,
								label: roleType,
							}))}
							disabled={metaDetails?.profile_language_mapping?.length === 0 || unsavedChanges}
						/>
					</div>
					<Divider className="my-4" />
				</>
			)}
			{/* {isMultiLingualSupportEnabled && (
					<>
						<div className="flex flex-col">
							<label className="text-[x-small] tertiary-color mb-1">{t("Language")}:</label>
							<Select
								value={interfaceLanguage?.code}
								title="Language"
								className="py-[0.2rem] custom-dropdown text-base-1 custom-select"
								onChange={handleLanguageChange}
								options={metaDetails?.languages?.map((lang: TopNavbarLanguage) => ({
									value: lang.code,
									label: lang.name,
								}))}
								disabled={metaDetails?.languages?.length <= 1}
							/>
						</div>
						<Divider className="my-4" />
					</>
				)} */}
			{/* <div className="flex">
							<span className="text-[x-small] tertiary-color cursor-pointer flex items-center mr-2">{t("Language")}: </span>
							<span className="text-base-1 primary-color font-medium">{interfaceLanguage?.name}</span>
				</div> 
				<Divider className="my-4" /> */}
			{/* <label className="text-[x-small] tertiary-color my-1">Theme:</label>
				<div className="flex gap-3 items-center">
				<div className="flex items-center justify-evenly mt-4">
						<span className="text-[x-small] text-[--neutral-black-opacity-50]">Dark</span>
						<Switch
							className="w-[30px]"
							unCheckedChildren={
								<div className="flex items-center w-full justify-start">
									<i className="material-symbols-outlined text-xl">light_mode</i>
								</div>
							}
							checkedChildren={
								<div className="flex items-center w-full justify-end">
									<i className="material-symbols-outlined text-xl">dark_mode</i>
								</div>
							}
							// checked={selectedThemeName === themeNames[0]}
							checked={localStorage.getItem("selectedThemeName") === themeNames[0]}
							onChange={handleThemeChange}
						/>
						<span className="text-[x-small] text-[--neutral-black]">Light</span>
						</div>
						<Select
						defaultValue={selectedThemeName || themeNames[0]}
						className="py-[0.2rem] custom-dropdown text-base-1 customTop-select"
						popupClassName="custom-dropdown"
						onChange={handleThemeChange}
						options={themeNames.map((theme) => ({ value: theme, label: theme.replace(/_/g, " ") }))}
						/>
				</div> */}

			{isProfileContextEnabled && (
				<>
					<div
						tabIndex={0}
						role="button"
						onClick={handleOpenProfileContext}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								handleOpenProfileContext();
							}
						}}
						className="flex items-center cursor-pointer hover:bg-[var(--neutral-black-opacity-5)] px-2 py-2 rounded-md transition-colors">
						<i className="material-symbols-outlined text-xl secondary-color mr-2">settings</i>
						<span className="text-base-1 secondary-color">{t("Profile Context")}</span>
					</div>
					<Divider className="my-4" />
				</>
			)}

			<div className="flex justify-start">
				<span className="text-lg font-medium tertiary-color cursor-pointer flex items-center">
					<div
						tabIndex={0}
						role="button"
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								handleLogOut();
							}
						}}
						onClick={() => handleLogOut()}
						className="flex items-center cursor-pointer">
						<i className="material-symbols-outlined primary-color text-2xl mr-1">logout</i>
						<span className="text-base-14 primary-color font-medium">{t("Log Out")}</span>
					</div>
				</span>
			</div>
		</div>
	);

	const ImportIcon = async () => {
		const logoName = environment.REACT_APP_PARTNERSHIP_LOGO_NAME;
		if (!logoName) {
			return;
		}

		try {
			const logoPath = await import(`public/icon/${logoName}.png`);
			if (!logoPath) {
				return;
			}
			setLogo(logoPath.default);
		} catch (err) {
			console.error("Error loading logo:", err);
			setError("Logo not found");
		}
	};

	useEffect(() => {
		ImportIcon();
	}, []);

	const redirectToHome = useCallback(() => {
		const handleHomeNavigation = () => {
			resetSearch();
			navigate(isAdminOrSupport ? "/admin-center" : "/");
		};

		if (unsavedChanges) {
			dispatch(
				setConfirmationData({
					status: confirmationStatusType.warning,
					editType: "",
					visible: true,
					name: reportsMenuItems.includes(currentPath) ? selectedReport?.name : studiesMenuItems.includes(currentPath) ? selectedStudy?.study_name : "",
					redirectFrom: confirmationTypes.type3,
				}),
			);
			setKey(isAdminOrSupport ? "/admin-center" : "/");
		} else {
			handleHomeNavigation();
		}
	}, [unsavedChanges, navigate, dispatch, currentPath, selectedReport, selectedStudy, isAdminOrSupport]);

	const handleAskKuralNavigation = useCallback(() => {
		if (!isAskKuralEnabled || isNavDisabled) {
			return;
		}

		if (unsavedChanges) {
			dispatch(
				setConfirmationData({
					status: confirmationStatusType.warning,
					editType: "",
					visible: true,
					name: reportsMenuItems.includes(currentPath) ? selectedReport?.name : studiesMenuItems.includes(currentPath) ? selectedStudy?.study_name : "",
					redirectFrom: confirmationTypes.type3,
				}),
			);
			setKey("/ask-kural");
		} else {
			navigate("/ask-kural");
		}
	}, [isAskKuralEnabled, isNavDisabled, unsavedChanges, navigate, dispatch, currentPath, selectedReport, selectedStudy]);

	const handleNavigationClick = useCallback(
		(e: React.MouseEvent) => {
			if (isNavDisabled) {
				return;
			}
			const clickedItem = menuItems.find((item) => t(item.label) === (e.target as HTMLElement).textContent);

			if (clickedItem && !clickedItem.disabled && clickedItem.value) {
				const targetPath = clickedItem.value;

				if (unsavedChanges) {
					dispatch(
						setConfirmationData({
							status: confirmationStatusType.warning,
							editType: "",
							visible: true,
							name: reportsMenuItems.includes(currentPath) ? selectedReport?.name : studiesMenuItems.includes(currentPath) ? selectedStudy?.study_name : "",
							redirectFrom: confirmationTypes.type3,
						}),
					);
					setKey(targetPath);
				} else {
					navigate(targetPath);
				}
			}
		},
		[menuItems, unsavedChanges, isNavDisabled, navigate, dispatch, t, currentPath, selectedReport, selectedStudy],
	);

	// Add confirmation handlers after handleThemeChange:
	const onConfirmNavigation = () => {
		if (key) {
			const keyLocal = key;
			setKey(null);
			if (reportsMenuItems.includes(currentPath)) {
				dispatch(setSelectedReport(null));
			}
			dispatch(setUnsavedChanges(false));
			dispatch(
				setAutosaveStatus({
					status: null,
					lastSaved: null,
				}),
			);
			dispatch(
				setConfirmationData({
					status: "",
					editType: "",
					visible: false,
					name: "",
					redirectFrom: "",
				}),
			);
			if (keyLocal === "/") {
				resetSearch();
			}
			navigate(keyLocal);
		}
	};

	const onCancelNavigation = useCallback(() => {
		dispatch(
			setConfirmationData({
				...confirmationData,
				visible: false,
			}),
		);
		setKey(null);
	}, [dispatch, confirmationData]);

	const handleOpenUserGuide = useCallback(() => {
		dispatch(setIsUserGuideModalVisible(true));
	}, []);

	const ITEM_HEIGHT_BASE = metaDetails?.profile_language_mapping?.length === 1 ? 40 : 35;
	const MAX_HEIGHT = 180;
	const itemCount = metaDetails?.profile_language_mapping?.length || 0;

	return (
		<Header className={`top-navbar flex justify-between items-center bg-[--neutral-white-opacity-90] ${useFigmaTopNav ? "top-navbar-figma" : ""}`}>
			{isLoggingOut &&
				createPortal(
					<div className="fixed inset-0 z-[2000] bg-[--neutral-white]" aria-busy="true">
						<Loading />
					</div>,
					document.body,
				)}
			{confirmationData?.visible && confirmationData?.redirectFrom === confirmationTypes.type3 && (
				<ConfirmationDialog confirmationData={confirmationData} onCancel={onCancelNavigation} onConfirm={onConfirmNavigation} />
			)}
			<div className="top-navbar-left flex items-center w-full justify-between gap-16">
				<div className="flex items-center">
					<span className="flex items-center">
						<DynamicLogo redirectToHome={redirectToHome} />
					</span>
					<div className="ml-7 mr-5">
						<Segmented
							className="top-navbar-navigation"
							onClick={handleNavigationClick}
							options={menuItems.map((item) => ({
								...item,
								label: item.tooltip ? (
									<Tooltip title={item.tooltip}>
										<span>{t(item.label)}</span>
									</Tooltip>
								) : (
									t(item.label)
								),
								disabled: item.disabled,
							}))}
							value={selectedMenuItem}
						/>
					</div>

					{!isAdminOrSupport && !isObserver && !isNavDisabled && isAskKuralEnabled && (
						<Button
							type="primary"
							className={`flex items-center ask-kural-btn mr-5 ${currentPath === "/ask-kural" ? "ask-kural-btn-selected" : ""}`}
							icon={
								currentPath === "/ask-kural" ? (
									<>
										<img src={AskKuralButtonLogoSelected} className="ask-kural-icon ask-kural-icon-selected" height={20} width={18} alt="" />
									</>
								) : (
									<>
										<img src={AskKuralButtonLogoDefault} className="ask-kural-icon ask-kural-icon-default" height={20} width={18} alt="" />
										<img src={AskKuralButtonLogoHover} className="ask-kural-icon ask-kural-icon-hover" height={20} width={18} alt="" />
									</>
								)
							}
							onClick={handleAskKuralNavigation}>
							{clientName ? (
								<>
									{t("Ask")} {t(clientName)}
								</>
							) : (
								t("Ask Kural")
							)}
						</Button>
					)}
				</div>
				{/* <Button
						onClick={() => {
							dispatch(resetAllChartsCacheFlat());
							dispatch(resetSelectedChartsForExportHash());
						}}>
						Clear Cache
					</Button> */}
				<div>
					{Logo && (
						<div className="client-right-logo-container items-center">
							<div className="logo-wrapper">
								<img src={String(Logo)} alt="Logo" className="client-logo" />
							</div>
							<Divider type="vertical" className="h-8 w-[1.5px] rounded bg-[#888b8d]" />
							<img src={String(ZSLogo)} alt="ZS Logo" className="zs-logo m-2" />
						</div>
					)}
				</div>

				<Space className="flex items-center space-x-0.5" />
				<div className="flex items-center gap-2">
					{isMultiLingualSupportEnabled && !isObserver && (
						<div className="flex flex-col">
							<Select
								value={interfaceLanguage?.code}
								prefix={<i className="flex items-start material-symbols-outlined text-xl secondary-color mr-1">translate</i>}
								title="Language"
								className="text-base-1 customTop-select"
								suffixIcon={<DownOutlined className="text-[--secondary-color] text-[0.6rem]" />}
								classNames={{ popup: { root: "customTop-dropdown" } }}
								variant="borderless"
								virtual={false}
								listHeight={256}
								popupMatchSelectWidth={false}
								onChange={handleLanguageChange}
								options={metaDetails?.languages?.map((lang: TopNavbarLanguage) => ({
									value: lang.code,
									label: lang.name,
								}))}
								disabled={metaDetails?.languages?.length <= 1 || unsavedChanges}
							/>
						</div>
					)}
					{!isAdminOrSupport && !isDeveloper && !isObserver && (
						<div className="flex ml-3 items-center">
							<Select
								key="user-profile"
								title="User profile"
								prefix={<i className="flex items-start material-symbols-outlined text-xl secondary-color">person</i>}
								suffixIcon={<DownOutlined className="text-[--secondary-color] text-[0.6rem]" />}
								value={userProfile}
								className="customTop-select"
								classNames={{ popup: { root: "customTop-dropdown" } }}
								placement="bottomRight"
								popupMatchSelectWidth={150}
								listHeight={9999}
								styles={{
									popup: {
										root: {
											height: itemCount * ITEM_HEIGHT_BASE > MAX_HEIGHT ? `${MAX_HEIGHT}px` : "auto",
											overflow: "auto",
										},
									},
								}}
								variant="borderless"
								virtual={false}
								onChange={handleProfileChange}
								options={metaDetails?.profile_language_mapping?.map((profile) => ({
									value: profile.profile_name,
									label: profile?.display_profile_name || profile.profile_name,
								}))}
								showSearch
								onSelect={handleProfileChange}
								disabled={unsavedChanges}
							/>
						</div>
					)}
					{isUserGuideEnabled && (
						<>
							<Button
								type="text"
								onClick={handleOpenUserGuide}
								style={{ width: "25.6px", height: "25.6px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
								icon={<i className="material-symbols-outlined text-[--primary-color]">help</i>}></Button>
							<Suspense fallback={<></>}>{isUserGuideModalVisible && <UserGuide visible={isUserGuideModalVisible} />}</Suspense>
						</>
					)}
					{isProfileContextEnabled && <Suspense fallback={<></>}>{isProfileContextModalVisible && <ProfileContext />}</Suspense>}

					{!isAdminOrSupport &&
						(isReflectiveModeCampaignEnabled ||
							(shouldShowNotficationPopup &&
								isAskKuralEnabled &&
								(askKuralVersion[selectedQueriesConfig] === AskKuralVersionType.v3_2 || askKuralVersion[selectedQueriesConfig] === AskKuralVersionType.v3_3))) && (
							<Notification />
						)}
					<Popover content={popContent} placement="bottomRight" trigger="click" open={open} onOpenChange={handleOpenChange} rootClassName="top-navbar-popover">
						<div className="flex items-center cursor-pointer space-x-2 ml-3">
							<Badge
								count={
									showLicenseIndicator ? (
										<span
											style={{
												display: "inline-flex",
												alignItems: "center",
												justifyContent: "center",
												width: "0.9rem",
												height: "0.9rem",
												borderRadius: "50%",
												background: APP_LICENSE_BANNER_COLOR,
												color: "var(--neutral-white)",
												fontSize: "0.6rem",
												fontWeight: 700,
												lineHeight: 1,
											}}>
											!
										</span>
									) : (
										0
									)
								}
								offset={[-2, 2]}
								title="">
								<Avatar
									className="navbar-icon cursor-pointer"
									style={{
										fontSize: "0.933333rem",
										height: "2.133333rem",
										width: "2.133333rem",
										border: "1px solid var(--neutral-grey)",
										backgroundColor: "var(--neutral-grey)",
										color: "var(--neutral-white)",
										fontWeight: 600,
									}}>
									{getInitials(metaDetails?.fullName)}
								</Avatar>
							</Badge>
						</div>
					</Popover>
				</div>
			</div>
		</Header>
	);
};

export default TopNavbar;
